import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useInView,
} from 'framer-motion';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-16px) rotate(0.5deg); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.75; transform: scale(1.06); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const gradientFlow = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const dotPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.4); opacity: 0.6; }
`;

const Hero = styled.section`
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  padding: var(--header-offset, calc(var(--nav-height) + 32px)) 0 56px;
  overflow: hidden;
  background: radial-gradient(ellipse 90% 70% at 70% 20%, rgba(109, 187, 26, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse 60% 50% at 10% 80%, rgba(14, 107, 60, 0.06) 0%, transparent 50%),
    #ffffff;

  @media (max-width: 968px) {
    min-height: auto;
    padding: calc(var(--nav-height) + 20px) 0 48px;
  }

  @media (max-width: 480px) {
    padding: calc(var(--nav-height) + 16px) 0 40px;
  }
`;

const Bg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;

  .mesh {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 80% 60% at 75% 25%, rgba(109, 187, 26, 0.12) 0%, transparent 55%),
      radial-gradient(ellipse 50% 40% at 20% 80%, rgba(14, 107, 60, 0.08) 0%, transparent 50%),
      radial-gradient(ellipse 30% 30% at 90% 70%, rgba(242, 183, 5, 0.06) 0%, transparent 50%);
  }

  .grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(14, 107, 60, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(14, 107, 60, 0.035) 1px, transparent 1px);
    background-size: 56px 56px;
    mask-image: radial-gradient(ellipse 100% 90% at 55% 45%, black 10%, transparent 72%);
  }

  .orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    animation: ${pulseGlow} 12s ease-in-out infinite;
    &.a { width: 560px; height: 560px; top: -20%; right: 0; background: rgba(109, 187, 26, 0.2); }
    &.b { width: 420px; height: 420px; bottom: -5%; left: -8%; background: rgba(14, 107, 60, 0.14); animation-delay: -5s; }
    &.c { width: 220px; height: 220px; top: 45%; left: 40%; background: rgba(242, 183, 5, 0.12); animation-delay: -2s; }
  }

  .ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(14, 107, 60, 0.06);
    &.r1 {
      width: 480px; height: 480px; top: 8%; right: 6%;
      animation: ${spin} 80s linear infinite;
    }
    &.r2 {
      width: 320px; height: 320px; top: 18%; right: 14%;
      border-color: rgba(109, 187, 26, 0.08);
      animation: ${spin} 60s linear infinite reverse;
    }
  }

  svg.lines {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0.35;
  }

  @media (max-width: 768px) {
    .ring {
      display: none;
    }
    .orb.a {
      width: 260px;
      height: 260px;
      right: -20%;
    }
    .orb.b {
      width: 200px;
      height: 200px;
      left: -15%;
    }
    .orb.c {
      display: none;
    }
  }
`;

const GridLayout = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1.02fr 0.98fr;
  align-items: center;
  gap: clamp(40px, 6vw, 72px);
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 var(--container-padding);
  width: 100%;
  position: relative;
  z-index: 2;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 28px;
    overflow: hidden;
  }

  @media (max-width: 480px) {
    gap: 24px;
    padding: 0 max(16px, var(--container-padding));
  }
`;

const LeftCol = styled.div`
  width: 100%;
  max-width: 100%;

  @media (max-width: 968px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 18px 8px 12px;
  margin-bottom: 28px;
  border-radius: 100px;
  background: linear-gradient(135deg, rgba(255,255,255,0.95), rgba(240,250,244,0.9));
  border: 1px solid rgba(14, 107, 60, 0.15);
  box-shadow: 0 4px 24px rgba(14, 107, 60, 0.08);
  font-size: 0.82rem;
  font-weight: 600;
  color: #0e6b3c;
  backdrop-filter: blur(12px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(109,187,26,0.08), transparent);
    background-size: 200% 100%;
    animation: ${shimmer} 4s linear infinite;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #6dbb1a;
    box-shadow: 0 0 0 4px rgba(109, 187, 26, 0.2);
    animation: ${dotPulse} 2s ease infinite;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 7px 14px 7px 10px;
    margin-bottom: 20px;
    max-width: 100%;
    text-align: center;
    justify-content: center;
  }

  @media (max-width: 400px) {
    font-size: 0.7rem;
    gap: 8px;
    line-height: 1.35;
    border-radius: 14px;
  }
`;

const Title = styled(motion.h1)`
  font-family: 'Poppins', var(--font-display);
  font-size: clamp(2.5rem, 5.8vw, 4rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.04em;
  color: #07111f;
  margin-bottom: 24px;
  max-width: 580px;

  .gradient {
    background: linear-gradient(90deg, #0e6b3c 0%, #6dbb1a 100%);
    background-size: 100% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientFlow} 8s ease infinite;
  }

  @media (max-width: 968px) {
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: clamp(1.65rem, 7.2vw, 2.15rem);
    margin-bottom: 16px;
    letter-spacing: -0.03em;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.02rem, 1.9vw, 1.15rem);
  line-height: 1.85;
  color: #64748b;
  max-width: 520px;
  margin-bottom: 20px;

  @media (max-width: 968px) {
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.7;
    margin-bottom: 16px;
    padding: 0 4px;
  }
`;

const BtnRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 40px;

  @media (max-width: 968px) {
    justify-content: center;
    margin-bottom: 28px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
    gap: 12px;
  }
`;

const PrimaryBtn = styled(motion(Link))`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 26px;
  border-radius: 100px;
  background: #0e6b3c;
  background-size: 200% auto;
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: none;
  box-shadow:
    0 4px 14px rgba(14, 107, 60, 0.25),
    0 12px 40px rgba(14, 107, 60, 0.3),
    inset 0 1px 0 rgba(255,255,255,0.2);
  position: relative;
  overflow: hidden;
  animation: ${gradientFlow} 6s ease infinite;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.2) 50%, transparent 60%);
    transform: translateX(-100%);
    animation: ${shimmer} 3s ease-in-out infinite;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 14px 20px;
    font-size: 0.9rem;
  }
`;

const GlassBtn = styled(motion(Link))`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 24px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(14, 107, 60, 0.12);
  color: #0f172a;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  backdrop-filter: blur(16px);
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.06);

  .play {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(14,107,60,0.12), rgba(109,187,26,0.15));
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0e6b3c;
    font-size: 0.7rem;
    box-shadow: 0 2px 8px rgba(14, 107, 60, 0.15);
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 14px 20px;
    font-size: 0.9rem;
  }
`;

const TrustRow = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }

  @media (max-width: 400px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  .avatars {
    display: flex;

    .av {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 2px solid #fff;
      margin-left: -10px;
      background: linear-gradient(145deg, #0e6b3c, #6dbb1a);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 0.65rem;
      font-weight: 800;
      box-shadow: 0 2px 8px rgba(14, 107, 60, 0.2);

      &:first-child { margin-left: 0; }
      &:nth-child(2) { background: linear-gradient(145deg, #6dbb1a, #0e6b3c); }
      &:nth-child(3) { background: linear-gradient(145deg, #0e6b3c, #15803d); }
      &:nth-child(4) {
        background: #e2e8f0;
        color: #64748b;
        font-size: 0.6rem;
      }
    }
  }

  .meta {
    .stars {
      color: #f2b705;
      font-size: 0.9rem;
      letter-spacing: 2px;
      margin-right: 8px;
    }

    strong {
      font-family: 'Poppins', sans-serif;
      font-size: 0.9rem;
      font-weight: 600;
      color: #0f172a;
    }
  }
`;

const Right = styled.div`
  position: relative;
  min-height: 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1400px;

  @media (max-width: 968px) {
    order: -1;
    min-height: 0;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    padding: 0 4px;
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const Halo = styled.div`
  position: absolute;
  width: 90%;
  height: 85%;
  border-radius: 32px;
  background: linear-gradient(135deg, rgba(14, 107, 60, 0.15), rgba(109, 187, 26, 0.1));
  filter: blur(40px);
  animation: ${pulseGlow} 6s ease-in-out infinite;

  @media (max-width: 768px) {
    width: 95%;
    height: 70%;
    filter: blur(28px);
  }
`;

const DashboardScene = styled(motion.div)`
  position: relative;
  width: min(100%, 540px);
  max-width: 100%;
  margin: 0 auto;
  transform-style: preserve-3d;

  @media (max-width: 968px) {
    width: 100%;
    max-width: 420px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const DashboardOuter = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  padding: 1px;
  background: linear-gradient(135deg, rgba(14,107,60,0.35), rgba(109,187,26,0.2), rgba(242,183,5,0.15));
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.5) inset,
    0 32px 80px -20px rgba(14, 107, 60, 0.25),
    0 16px 40px rgba(15, 23, 42, 0.08);
  animation: ${float} 8s ease-in-out infinite;

  @media (max-width: 768px) {
    animation: none;
    border-radius: 18px;
  }
`;

const Dashboard = styled.div`
  background: #fff;
  border-radius: 23px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 60px 1fr;
  min-height: 360px;

  @media (max-width: 768px) {
    min-height: 300px;
    grid-template-columns: 48px 1fr;
    border-radius: 17px;
  }

  @media (max-width: 400px) {
    min-height: 260px;
    grid-template-columns: 40px 1fr;
  }
`;

const Sidebar = styled.div`
  background: linear-gradient(180deg, #0e6b3c 0%, #065f32 50%, #054d28 100%);
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  .logo-mark {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(255,255,255,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 800;
    font-size: 0.85rem;
    margin-bottom: 16px;
    border: 1px solid rgba(255,255,255,0.2);
  }

  i {
    color: rgba(255, 255, 255, 0.45);
    font-size: 0.9rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    transition: all 0.25s;
    cursor: default;

    &.active {
      background: rgba(255, 255, 255, 0.18);
      color: #fff;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }

    &:hover:not(.active) {
      color: rgba(255,255,255,0.8);
      background: rgba(255,255,255,0.08);
    }
  }

  @media (max-width: 768px) {
    padding: 14px 0;
    gap: 4px;

    .logo-mark {
      width: 28px;
      height: 28px;
      font-size: 0.7rem;
      margin-bottom: 10px;
    }

    i {
      width: 32px;
      height: 32px;
      font-size: 0.75rem;
      border-radius: 8px;
    }
  }

  @media (max-width: 400px) {
    i:nth-child(n + 5) {
      display: none;
    }
  }
`;

const MainPanel = styled.div`
  padding: 20px 20px 18px;
  background: linear-gradient(180deg, #fafcfb 0%, #f4f9f6 100%);
  min-width: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 12px 10px 10px;
  }
`;

const PanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h3 {
    font-family: 'Poppins', sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
  }

  .search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: 10px;
    background: #fff;
    border: 1px solid #e2e8f0;
    font-size: 0.65rem;
    color: #94a3b8;
    min-width: 120px;
    i { color: #0e6b3c; font-size: 0.7rem; }
  }

  @media (max-width: 520px) {
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;

    h3 {
      font-size: 0.78rem;
      width: 100%;
    }

    .search {
      display: none;
    }
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 16px;

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }

  @media (max-width: 360px) {
    gap: 6px;
  }
`;

const StatCard = styled(motion.div)`
  padding: 12px 10px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e8f0eb;
  box-shadow: 0 2px 8px rgba(14, 107, 60, 0.04);
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(14, 107, 60, 0.1);
    border-color: rgba(14, 107, 60, 0.2);
  }

  .ico {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(14,107,60,0.1), rgba(109,187,26,0.12));
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0e6b3c;
    font-size: 0.65rem;
    margin-bottom: 8px;
  }

  small {
    display: block;
    font-size: 0.6rem;
    color: #94a3b8;
    margin-bottom: 4px;
    font-weight: 500;
  }

  strong {
    font-family: 'Poppins', sans-serif;
    font-size: 1.05rem;
    font-weight: 800;
    background: linear-gradient(135deg, #0e6b3c, #6dbb1a);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 400px) {
    padding: 8px 6px;
    border-radius: 10px;

    .ico {
      width: 22px;
      height: 22px;
      font-size: 0.55rem;
      margin-bottom: 4px;
    }

    small {
      font-size: 0.52rem;
    }

    strong {
      font-size: 0.85rem;
    }
  }
`;

const ChartBox = styled.div`
  background: #fff;
  border: 1px solid #e8f0eb;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 12px rgba(14, 107, 60, 0.04);
  position: relative;

  .chart-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    span:first-child {
      font-family: 'Poppins', sans-serif;
      font-size: 0.75rem;
      font-weight: 700;
      color: #0f172a;
    }
    .badge {
      padding: 4px 10px;
      border-radius: 100px;
      background: rgba(14, 107, 60, 0.08);
      color: #0e6b3c;
      font-size: 0.68rem;
      font-weight: 700;
    }
  }

  svg { width: 100%; height: 88px; display: block; }

  .chart-dot {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #6dbb1a;
    border: 2px solid #fff;
    box-shadow: 0 0 12px rgba(109, 187, 26, 0.6);
    right: 8%;
    top: 38%;
    animation: ${dotPulse} 2s ease infinite;
  }

  @media (max-width: 520px) {
    padding: 12px;
    margin-bottom: 10px;

    svg {
      height: 64px;
    }

    .chart-head span:first-child {
      font-size: 0.68rem;
    }
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.68rem;
    color: #64748b;
    padding: 10px 12px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid #f1f5f3;
    transition: border-color 0.2s;

    &:hover { border-color: rgba(14, 107, 60, 0.2); }

    @media (max-width: 480px) {
      font-size: 0.6rem;
      padding: 8px 10px;
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: linear-gradient(135deg, #6dbb1a, #0e6b3c);
      flex-shrink: 0;
      box-shadow: 0 0 8px rgba(109, 187, 26, 0.4);
    }
  }
`;

const FloatWidget = styled(motion.div)`
  position: absolute;
  z-index: 10;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(24px);
  box-shadow:
    0 8px 32px rgba(15, 23, 42, 0.08),
    0 0 0 1px rgba(14, 107, 60, 0.06);
  animation: ${floatSlow} 6s ease-in-out infinite;

  .head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }

  .icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    color: #fff;
    background: linear-gradient(135deg, #0e6b3c, #6dbb1a);
  }

  strong {
    display: block;
    font-family: 'Poppins', sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
    color: #0f172a;
  }
  .val {
    font-family: 'Poppins', sans-serif;
    font-size: 1.1rem;
    font-weight: 800;
    color: #0e6b3c;
  }
  span.sub { font-size: 0.65rem; color: #94a3b8; }

  .mini-chart {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    height: 28px;
    margin-top: 4px;
    div {
      flex: 1;
      max-width: 6px;
      border-radius: 3px 3px 0 0;
      background: linear-gradient(180deg, #6dbb1a, #0e6b3c);
    }
  }

  &.w1 { top: -2%; left: -10%; animation-delay: 0s; }
  &.w2 { top: 6%; right: -8%; animation-delay: -2s; }
  &.w3 { bottom: 12%; right: -6%; animation-delay: -3.5s; }
  &.w4 {
    bottom: -4%;
    left: 5%;
    animation-delay: -1s;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    .icon-wrap { width: 32px; height: 32px; font-size: 0.75rem; background: linear-gradient(135deg, #f2b705, #0e6b3c); }
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    border-radius: 14px;

    &.w1 {
      top: 2%;
      left: 2%;
      max-width: 140px;
    }
    &.w2,
    &.w3 {
      display: none;
    }
    &.w4 {
      bottom: 2%;
      left: 2%;
      right: auto;
      max-width: 160px;
    }

    .val {
      font-size: 0.95rem;
    }
    strong {
      font-size: 0.72rem;
    }
  }

  @media (max-width: 520px) {
    &.w1,
    &.w4 {
      display: none;
    }
  }
`;

const chartPath = 'M0,75 L25,62 L50,68 L75,42 L100,48 L125,28 L150,35 L175,18 L200,12 L220,8';
const chartArea = `${chartPath} L220,88 L0,88 Z`;

const statMeta = [
  { icon: 'fas fa-folder-open', label: 'Total Projects', key: 'projects', suffix: '+' },
  { icon: 'fas fa-users', label: 'Active Clients', key: 'clients', suffix: '+' },
  { icon: 'fas fa-check-circle', label: 'Completed', key: 'completed', suffix: '+' },
  { icon: 'fas fa-chart-line', label: 'Revenue Growth', key: 'growth', suffix: '%' },
];

function useCountUp(end, active, duration = 2200) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let n = 0;
    const step = end / (duration / 16);
    const t = setInterval(() => {
      n += step;
      if (n >= end) { setVal(end); clearInterval(t); }
      else setVal(Math.floor(n));
    }, 16);
    return () => clearInterval(t);
  }, [end, active, duration]);
  return val;
}

function MagneticButton({ children, ...props }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 280, damping: 22 });
  const sy = useSpring(y, { stiffness: 280, damping: 22 });

  return (
    <PrimaryBtn
      ref={ref}
      style={{ x: sx, y: sy }}
      onMouseMove={(e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        x.set((e.clientX - r.left - r.width / 2) * 0.12);
        y.set((e.clientY - r.top - r.height / 2) * 0.12);
      }}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      {children}
    </PrimaryBtn>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] },
  }),
};

const NewHero = () => {
  const statsRef = useRef(null);
  const inView = useInView(statsRef, { once: true, margin: '-40px' });
  const projects = useCountUp(50, inView);
  const clients = useCountUp(30, inView);
  const completed = useCountUp(120, inView);
  const growth = useCountUp(85, inView);
  const values = { projects, clients, completed, growth };

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 968
  );

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [6, -6]), { stiffness: 100, damping: 22 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), { stiffness: 100, damping: 22 });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 968);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (window.innerWidth < 968) return;
      mouseX.set(e.clientX / window.innerWidth - 0.5);
      mouseY.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, [mouseX, mouseY]);

  return (
    <Hero id="hero">
      <Bg aria-hidden="true">
        <div className="mesh" />
        <div className="orb a" />
        <div className="orb b" />
        <div className="ring r1" />
        <div className="ring r2" />
      </Bg>

      <GridLayout>
        <LeftCol>
          <Badge initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <span className="dot" />
            <i className="fas fa-check" style={{ marginRight: 6 }} />
            Trusted Software Development Company
          </Badge>

          <Title initial="hidden" animate="visible" variants={fadeUp} custom={1}>
            Build Powerful{' '}
            <span className="gradient">Digital Solutions</span>
            <br />
            That Grow Businesses
          </Title>

          <Subtitle initial="hidden" animate="visible" variants={fadeUp} custom={2}>
            We build modern websites, CRM, ERP, e-commerce platforms, and custom software that help
            businesses grow online with confidence.
          </Subtitle>

          <BtnRow initial="hidden" animate="visible" variants={fadeUp} custom={3}>
            <MagneticButton to="/contact">
              Get Free Consultation <i className="fas fa-plus" />
            </MagneticButton>
            <GlassBtn to="/projects" whileHover={{ scale: 1.03, y: -3 }} whileTap={{ scale: 0.98 }}>
              View Our Work <i className="fas fa-chevron-right" style={{ fontSize: '0.75rem' }} />
            </GlassBtn>
          </BtnRow>

          <TrustRow initial="hidden" animate="visible" variants={fadeUp} custom={4}>
            <div className="avatars">
              <div className="av">RS</div>
              <div className="av">AK</div>
              <div className="av">PM</div>
              <div className="av">DV</div>
            </div>
            <div className="meta">
              <span className="stars">★★★★★</span>
              <strong>Trusted by 50+ Clients</strong>
            </div>
          </TrustRow>
        </LeftCol>

        <Right>
          <Halo />
          <DashboardScene
            style={isMobile ? undefined : { rotateX, rotateY }}
            initial={{ opacity: 0, y: 48, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <FloatWidget className="w1" initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 }}>
              <div className="head">
                <div className="icon-wrap"><i className="fas fa-smile" /></div>
                <div>
                  <strong>Client Satisfaction</strong>
                  <span className="sub">This month</span>
                </div>
              </div>
              <div className="val">98%</div>
              <div className="mini-chart">
                {[35, 55, 45, 70, 60, 85, 75, 90].map((h, i) => (
                  <div key={i} style={{ height: `${h * 0.28}px` }} />
                ))}
              </div>
            </FloatWidget>

            <FloatWidget className="w2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 }}>
              <div className="head">
                <div className="icon-wrap" style={{ background: 'linear-gradient(135deg, #6dbb1a, #0e6b3c)' }}>
                  <i className="fas fa-chart-line" />
                </div>
                <div>
                  <strong>Revenue Growth</strong>
                  <span className="sub">YoY</span>
                </div>
              </div>
              <div className="val" style={{ marginBottom: 8 }}>85%</div>
              <div className="mini-chart">
                {[40, 65, 50, 80, 70, 95].map((h, i) => (
                  <div key={i} style={{ height: `${h * 0.28}px` }} />
                ))}
              </div>
            </FloatWidget>

            <FloatWidget className="w3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}>
              <div className="head">
                <div className="icon-wrap" style={{ background: 'linear-gradient(135deg, #0e6b3c, #065f32)' }}>
                  <i className="fas fa-headset" />
                </div>
                <div>
                  <strong>Support 24/7</strong>
                  <span className="sub">Always on</span>
                </div>
              </div>
            </FloatWidget>

            <FloatWidget className="w4" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}>
              <div className="icon-wrap"><i className="fas fa-bell" /></div>
              <div>
                <strong>New project</strong>
                <span className="sub">CRM deployed ✓</span>
              </div>
            </FloatWidget>

            <DashboardOuter ref={statsRef}>
              <Dashboard>
                <Sidebar>
                  <div className="logo-mark">R</div>
                  <i className="fas fa-th-large active" />
                  <i className="fas fa-folder" />
                  <i className="fas fa-users" />
                  <i className="fas fa-chart-pie" />
                  <i className="fas fa-cog" />
                </Sidebar>
                <MainPanel>
                  <PanelHead>
                    <h3>Dashboard Overview</h3>
                    <div className="search"><i className="fas fa-search" /> Search...</div>
                  </PanelHead>
                  <StatGrid>
                    {statMeta.map((s, i) => (
                      <StatCard
                        key={s.key}
                        initial={{ opacity: 0, y: 16 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.5 + i * 0.08 }}
                      >
                        <div className="ico"><i className={s.icon} /></div>
                        <small>{s.label}</small>
                        <strong>{values[s.key]}{s.suffix}</strong>
                      </StatCard>
                    ))}
                  </StatGrid>
                  <ChartBox>
                    <div className="chart-head">
                      <span>Project Growth</span>
                      <span className="badge">{growth}% ↑ Growth</span>
                    </div>
                    <div className="chart-dot" />
                    <svg viewBox="0 0 220 88" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="heroChartFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#6dbb1a" stopOpacity="0.35" />
                          <stop offset="100%" stopColor="#6dbb1a" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id="heroChartLine" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#0e6b3c" />
                          <stop offset="100%" stopColor="#6dbb1a" />
                        </linearGradient>
                      </defs>
                      {[22, 44, 66, 88].map((y) => (
                        <line key={y} x1="0" y1={y} x2="220" y2={y} stroke="#f1f5f3" strokeWidth="0.5" />
                      ))}
                      <motion.path
                        d={chartArea}
                        fill="url(#heroChartFill)"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8, duration: 1 }}
                      />
                      <motion.path
                        d={chartPath}
                        fill="none"
                        stroke="url(#heroChartLine)"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ delay: 0.5, duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
                      />
                    </svg>
                  </ChartBox>
                  <ActivityList>
                    {[
                      'New CRM project kicked off',
                      'E-commerce store went live',
                      'Client revenue milestone hit',
                    ].map((text) => (
                      <div key={text} className="item">
                        <span className="dot" />
                        {text}
                      </div>
                    ))}
                  </ActivityList>
                </MainPanel>
              </Dashboard>
            </DashboardOuter>
          </DashboardScene>
        </Right>
      </GridLayout>

    </Hero>
  );
};

export default NewHero;
